// UploadWorksheet.js
import { useEffect, useState } from 'react'
import styles from './UploadWorksheet.module.scss'
import { useApi } from '../../../api/api'
import { useDropzone } from 'react-dropzone'
import { useSelector } from 'react-redux'
import { UploadSimpleIcon, FileIcon, TrashIcon } from '@phosphor-icons/react'
import {
    ScoopButton,
    ScoopTextField,
    ScoopDialog,
    ScoopLinearProgress,
    ScoopRow,
    ScoopColumn,
    useToast,
} from '../../ui'

// when user uploads an image to S3, make sure the filename is safe
function safeS3FileName(str) {
    return str.replace(/[^a-zA-Z0-9._-]/g, '_').replace(/ /g, '_')
}

export default function UploadWorksheet({ isOpen, onClose, onRefresh }) {
    const toast = useToast()
    const {
        acceptedFiles,
        fileRejections,
        getRootProps,
        getInputProps,
        isFocused,
        isDragAccept,
        isDragReject,
    } = useDropzone({
        accept: {
            // 'application/vnd.ms-excel': [],
            'text/xlsx': [],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [],
        },
    })

    // Hook to fetch data
    const { postData: postDataDefault } = useApi()
    const { postData: postDataWorkspaceactions } = useApi(
        'https://pig8gecvvk.execute-api.us-west-2.amazonaws.com/corsair/workspaceactions'
    )
    const workspaceID = useSelector((state) => state.auth.workspaceID)
    const userID = useSelector((state) => state.auth.userID)
    const [isLoading, setIsLoading] = useState(false)
    const [label, setLabel] = useState('')
    const [description, setDescription] = useState('')
    const [disableUpload, setDisableUpload] = useState(false)

    const acceptedFileItems = acceptedFiles.map((file) => (
        <li key={file.path}>
            {file.path} - {file.size} bytes
        </li>
    ))

    const dropzoneClassName = `${styles.dropzone} ${isFocused ? styles.dropzoneFocused : ''} ${isDragAccept ? styles.dropzoneAccept : ''} ${isDragReject ? styles.dropzoneReject : ''}`

    const [step, setStep] = useState(0) // 0 for file upload, 1 for additional details

    const handleNext = () => {
        setStep(1)
    }

    const handleBack = () => {
        setStep(0)
    }

    useEffect(() => {
        if (acceptedFiles.length > 0 && label === '') {
            setLabel(acceptedFiles[0].name.replace('.pptx', ''))
        }
    }, [acceptedFiles])

    const handleUpload = async () => {
        setProgress(1) // Initiate the progress
        setDisableUpload(true)
        // Create the payload based on selected option
        let payload = {
            action: 'uploadSpreadsheet',
            workspaceID: workspaceID,
            isDev: process.env.REACT_APP_SCOOP_ENV === 'dev',
        }

        try {
            const presignedData = await postDataWorkspaceactions({
                ...payload,
                fileName: safeS3FileName(acceptedFiles[0].name),
                fileType: acceptedFiles[0].type,
            })

            await fetch(presignedData.signedRequest, {
                method: 'PUT',
                body: acceptedFiles[0],
                headers: {
                    'Content-Type': acceptedFiles[0].type,
                },
            })

            setProgress(100)
            toast.success(
                'Spreadsheet file uploaded successfully! Please wait a few moments for the new worksheet to be created.'
            )

            // Directly call the creation of canvas from presentation
            await createWorksheetFromUpload()
        } catch (error) {
            console.error('Error uploading the file:', error)
            setProgress(0) // Reset the progress
            toast.error('Error uploading file')
            setTimeout(() => {
                onClose()
            }, 3000)
        }
    }

    const createWorksheetFromUpload = async () => {
        setIsLoading(true)
        const action = {
            action: 'createLiveWorksheetFromExcel',
            workspaceID: workspaceID,
            userID: userID,
            label: label,
            description: description,
            fileName: safeS3FileName(acceptedFiles[0].name),
        }

        try {
            const result = await postDataDefault(action)
            if (result?.worksheetID) {
                // Success
                // dispatch({
                //     type: 'APPLY_LOADED_OBJECTS_STATE',
                //     payload: [],
                // });
                toast.success('You will see your spreadsheet as a worksheet momentarily.')
                setTimeout(() => {
                    onRefresh()
                }, 5000)
            }
            setIsLoading(false)
        } catch (error) {
            console.error('Error creating worksheet from spreadsheet:', error)
        }
    }

    const [progress, setProgress] = useState(-1)

    useEffect(() => {
        let timer
        if (progress > 0 && progress < 100) {
            timer = setInterval(() => {
                setProgress((prevProgress) => Math.min(prevProgress + 3, 100))
            }, 1000)
        } else if (progress >= 100) {
            clearInterval(timer)
        }
        return () => {
            clearInterval(timer)
        }
    }, [progress])

    const dialogActions = (
        <ScoopRow className={styles.dialogActions}>
            {step === 0 && (
                <>
                    <ScoopButton variant="text" size="sm" onClick={onClose}>
                        Cancel
                    </ScoopButton>
                    <ScoopButton
                        variant="default"
                        size="sm"
                        onClick={handleNext}
                        disabled={acceptedFiles.length === 0}
                    >
                        Next
                    </ScoopButton>
                </>
            )}
            {step === 1 && (
                <>
                    <ScoopButton variant="text" size="sm" onClick={handleBack}>
                        Back
                    </ScoopButton>
                    <ScoopButton
                        variant="default"
                        size="sm"
                        onClick={handleUpload}
                        disabled={!label || disableUpload}
                    >
                        Upload
                    </ScoopButton>
                </>
            )}
        </ScoopRow>
    )

    return (
        <>
            <ScoopDialog
                open={isOpen}
                onClose={onClose}
                title={'Upload a Spreadsheet file'}
                actions={dialogActions}
                className={styles.dialog}
            >
                {step === 0 && (
                    <section className={styles.container}>
                        <div {...getRootProps({ className: dropzoneClassName })}>
                            <input {...getInputProps()} />
                            <UploadSimpleIcon size={48} className={styles.uploadIcon} />
                            <div className={styles.dropzoneText}>.XLSX</div>
                            <div className={styles.dropzoneMainText}>
                                Drag 'n' drop your data file here or{' '}
                            </div>
                            <a className={styles.dropzoneLink}>click here to select</a>
                        </div>
                        {acceptedFiles.length > 0 && (
                            <ul className={styles.fileList}>{acceptedFileItems}</ul>
                        )}
                    </section>
                )}

                {step === 1 && (
                    <section className={styles.container}>
                        <div>
                            {acceptedFiles.map((file) => (
                                <ScoopColumn
                                    className={styles.filePreviewContainer}
                                    key={file.path}
                                >
                                    <ScoopRow className={styles.filePreviewHeader}>
                                        <FileIcon size={20} />
                                        <div className={styles.filePreviewName}>{file.path}</div>
                                        <TrashIcon
                                            size={18}
                                            className={styles.filePreviewDeleteIcon}
                                        />
                                    </ScoopRow>
                                    <div className={styles.filePreviewInputWrapper}>
                                        <ScoopTextField
                                            placeholder={'Spreasheet name'}
                                            value={label}
                                            onChange={(event) => setLabel(event.target.value)}
                                        />
                                    </div>
                                    <div className={styles.filePreviewInputWrapper}>
                                        <ScoopTextField
                                            placeholder={'Spreadsheet description'}
                                            value={description}
                                            onChange={(event) => setDescription(event.target.value)}
                                        />
                                    </div>
                                </ScoopColumn>
                            ))}
                        </div>

                        {progress !== -1 && !isLoading && (
                            <ScoopLinearProgress
                                variant="determinate"
                                value={progress}
                                className={styles.progressBar}
                            />
                        )}
                    </section>
                )}
                {isLoading && (
                    <ScoopLinearProgress variant="indeterminate" className={styles.progressBar} />
                )}
            </ScoopDialog>
        </>
    )
}
